// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import qs from 'query-string'
import moment from 'moment'

import {
  TabbedView,
  FilterSidebar,
  PicklistWithData,
  DynamicPicklist,
  DateRange,
  Input,
  Messages,
  SvgIcon,
  EmployerWhatsAppMessages,
  Search
} from '../../'

import {
  decodeBase64,
  hasDateChanged,
  normalizePhoneNumber
} from '../../../utils'

import {
  CONTENTTYPE_MESSAGE,
  CONTENTTYPE_WHATSAPPMESSAGE
} from '../../../constants'

import { QUERY_EMPLOYERGROUP_EMPLOYERS } from '../../../queries'


class EmployerGroupCommunications extends React.Component {
  static propTypes = {
    identityId: PropTypes.string,
    employerIdentityId: PropTypes.string,
    communicationType: PropTypes.oneOf(['SMS', 'WhatsApp']).isRequired,
    fromAddr: PropTypes.string,
    toAddr: PropTypes.string,
    urlPath: PropTypes.string,
    title: PropTypes.string,
    noStatusFilter: PropTypes.bool,
    lockOnCommunicationType: PropTypes.bool
  }

  static defaultProps = {
    urlPath: '/os/communications',
    title: <React.Fragment>
        <SvgIcon
          variant='utility'
          symbol='comments'
          colour='light'
          size='large'
          className='slds-m-right_small'
        />
        <div className='slds-m-top_x-small u-font-size-x-large u-text-caps'>
          Communications
        </div>
      </React.Fragment>,
    lockOnCommunicationType: false
  }

  state = {
    // shared filters
    employerFilter: '',
    communicationType: this.props.lockOnCommunicationType
      ? this.props.communicationType
      : null,
    statusFilter: null,
    directionFilter: 'inbound',  // default
    // scheduledAtFromFilter: null,
    // scheduledAtToFilter: null,
    orderBy: this.props.communicationType === 'SMS'
      ? '-timestamp'
      : '-created_at',  // 'WhatsApp'
    messageTypeFilter: null,
    createdAtFromFilter: null,
    createdAtToFilter: null,
    // unknownParticipantFilter: false,
    // message filters
    timestampFromFilter: null,
    timestampToFilter: null,
    fromAddrFilter: undefined,
    toAddrFilter: undefined,
    // whatsapp filters
    searchString: '',
    searchFilter: '',
    // case creation
    productType: null,
    requestType: null,
    assignedTo: null,
    nextActionAt: undefined,
    priority: 'B',
    caseNote: '',
    followUp: false,
    selectedMessage: null,
    selectedMessageIdentity: null,
    affectsEmployer: null
  }

  componentDidMount = () => {
    // this life-cycle function implementation ensures that, on first render and/or
    // returning from another page, that filters will still be set to url and what it was before
    if (this.props.location.search) {
      this.setFiltersFromQueryString(this.props.location.search)
    } else {
      this.setQueryString(this.state, true)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search) {
      this.setFiltersFromQueryString(nextProps.location.search)
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // NOTE: update query string without causing re-render
    let isAnyFilterSet = nextState.employerFilter ||
      nextState.statusFilter ||
      nextState.directionFilter ||
      nextState.messageTypeFilter ||
      nextState.searchFilter ||
      nextState.createdAtFromFilter ||
      nextState.createdAtToFilter ||
      // nextState.scheduledAtFromFilter ||
      // nextState.scheduledAtToFilter ||
      nextState.timestampFromFilter ||
      nextState.timestampToFilter ||
      // nextState.unknownParticipantFilter ||
      nextState.orderBy

    let hasAnyFilterChanged = this.state.communicationType !== nextState.communicationType ||
      this.state.employerFilter !== nextState.employerFilter ||
      this.state.statusFilter !== nextState.statusFilter ||
      this.state.directionFilter !== nextState.directionFilter ||
      this.state.messageTypeFilter !== nextState.messageTypeFilter ||
      this.state.searchFilter !== nextState.searchFilter ||
      hasDateChanged(this.state.createdAtFromFilter, nextState.createdAtFromFilter) ||
      hasDateChanged(this.state.createdAtToFilter, nextState.createdAtToFilter) ||
      // hasDateChanged(this.state.scheduledAtFromFilter, nextState.scheduledAtFromFilter) ||
      // hasDateChanged(this.state.scheduledAtToFilter, nextState.scheduledAtToFilter) ||
      hasDateChanged(this.state.timestampFromFilter, nextState.timestampFromFilter) ||
      hasDateChanged(this.state.timestampToFilter, nextState.timestampToFilter) ||
      // this.state.unknownParticipantFilter !== nextState.unknownParticipantFilter ||
      this.state.orderBy !== nextState.orderBy

    if (this.props.location.search === '' && isAnyFilterSet) {
      this.setQueryString(nextState)
      return false
    }
    else if (Object.is(this.props.location.search, nextProps.location.search) && hasAnyFilterChanged) {
      this.setQueryString(nextState)
      return false
    }
    else if (hasAnyFilterChanged) {
      this.setQueryString(nextState)
      return false
    }

    return true
  }

  setFiltersFromQueryString = (queryString) => {
    let queryParams = qs.parse(queryString)

    let filters = Object.assign(
      {},
      ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
        key === 'orderBy'
          ? { orderBy: queryParams[key] }
          : key === 'type'
            ? { communicationType: queryParams.type }
            : key === 'unknownParticipant'
              ? { unknownParticipantFilter: true }
              : [  'createdAtFrom', 'createdAtTo', 'timestampFrom', 'timestampTo' ].indexOf(key) > -1
                ? { [`${key}Filter`]: moment(queryParams[key], 'YYYYMMDDTHHmm') }
                : { [`${key}Filter`]: queryParams[key] }
      ))
    )
    this.setState(filters)
  }

  setQueryString = (state, setDefault) => {
    let filters = {
      orderBy: state.orderBy
    }

    if (this.props.communicationType) {
      filters.type = this.props.communicationType
    } else {
      if (setDefault) {
        filters.type = 'SMS'
      } else if (state.communicationType) {
        filters.type = state.communicationType
      }
    }

    if (!!state.employerFilter) {
      filters.employer = state.employerFilter
    }

    if (state.statusFilter) {
      filters.status = state.statusFilter
    }

    if (state.directionFilter) {
      filters.direction = state.directionFilter
    }

    if (state.messageTypeFilter) {
      filters.messageType = state.messageTypeFilter
    }

    if (state.searchFilter) {
      filters.searchFilter = state.searchFilter
    }

    if (state.fromAddrFilter) {
      filters.fromAddr = state.fromAddrFilter
    }

    if (state.toAddrFilter) {
      filters.toAddr = state.toAddrFilter
    }

    if (setDefault && state.communicationType === 'SMS') {
      filters.timestampFrom = moment().subtract(7, 'days').format('YYYYMMDDTHHmm')
    } else if (state.timestampFromFilter) {
      filters.timestampFrom = state.timestampFromFilter.format('YYYYMMDDTHHmm')
    }
    if (state.timestampToFilter) {
      filters.timestampTo = state.timestampToFilter.format('YYYYMMDDTHHmm')
    }

    if (state.createdAtFromFilter) {
      filters.createdAtFrom = state.createdAtFromFilter.format('YYYYMMDDTHHmm')
    }
    if (state.createdAtToFilter) {
      filters.createdAtTo = state.createdAtToFilter.format('YYYYMMDDTHHmm')
    }

    if (state.unknownParticipantFilter) {
      filters.unknownParticipant = 'yes'
    }

    this.props.history.push({
      pathname: this.props.urlPath,
      search: qs.stringify(filters)
    })
  }

  changeDirectionFilter = (direction) => {
    if (this.state.communicationType === 'WhatsApp') {
      this.setState({
        directionFilter: direction,
        orderBy: '-created_at'
      })
    }
    // 'SMS' as communication type
    else if (direction === 'inbound') {
      this.setState({
        directionFilter: direction,
        orderBy: '-timestamp'
      })
    } else {
      this.setState({
        directionFilter: direction,
        orderBy: '-scheduled_at'
      })
    }
  }

  // handleParticipantCategoryChoice = (category) => {
  //   this.setState({ unknownParticipantFilter: category === 'Unknown' })
  // }

  changeOrderBy = (orderBy) => this.setState({ orderBy })

  getFilters = (communicationType) => {
    if (communicationType === 'SMS') {
      return this.getMessageFilters()
    }

    if (communicationType === 'WhatsApp') {
      return this.getWhatsAppMessageFilters()
    }

    return []
  }

  getMessageFilters = () => {
    let filters = [
      {
        title: 'Employer',
        options: <DynamicPicklist
          id='employer'
          query={QUERY_EMPLOYERGROUP_EMPLOYERS}
          field='friendlyName'
          params={{
            employerGroupId: decodeBase64(this.props.employerGroupId)
          }}
          defaultValue='All'
          value={this.state.employerFilter
            ? this.state.employerFilter
            : 'all'
          }
          onChange={employer => (
            this.setState({
              employerFilter: employer === 'all'
                ? null
                : employer
            })
          )}
        />
      },
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='MessageStatus'
          default='All'
          symbol='chevrondown'
          value={this.state.statusFilter
            ? this.state.statusFilter
            : 'all'
          }
          onChange={status => this.setState({
            statusFilter: status === 'all' ? null : status
          })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Direction',
        options: <PicklistWithData
          id='direction'
          forField='MessageDirection'
          default='Inbound'
          symbol='chevrondown'
          value={this.state.directionFilter}
          onChange={this.changeDirectionFilter}
          closeDropdownOnSelect={true}
          inline={true}
          excludeOptions={this.props.identityId ? ['internal'] : null}
        />
      },
      {
        title: 'Message type',
        options: <PicklistWithData
          id='messageType'
          forField='MessageMessageType'
          default='All'
          symbol='chevrondown'
          value={this.state.messageTypeFilter
            ? this.state.messageTypeFilter
            : 'all'
          }
          onChange={messageType => this.setState({
            messageTypeFilter: messageType === 'all' ? null : messageType
          })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'From number',
        options: <Input
          id='fromAddr'
          value={this.state.fromAddrFilter}
          onChange={e => this.setState({ fromAddrFilter: e.target.value})}
        />
      },
      {
        title: 'To number',
        options: <Input
          id='toAddr'
          value={this.state.toAddrFilter}
          onChange={e => this.setState({ toAddrFilter: e.target.value})}
        />
      },
      {
        title: 'Timestamp between',  // sent or received
        options: <DateRange
          startDate={this.state.timestampFromFilter}
          endDate={this.state.timestampToFilter}
          onStartDateChange={date => this.setState({ timestampFromFilter: date })}
          onEndDateChange={date => this.setState({ timestampToFilter: date })}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      }
    ]

    // if (this.props.identityId) {
    //   filters.shift()
    // }


    return filters
  }

  getWhatsAppMessageFilters = () => {
    let filters = [
      {
        title: 'Employer',
        options: <DynamicPicklist
          id='employer'
          query={QUERY_EMPLOYERGROUP_EMPLOYERS}
          field='friendlyName'
          params={{
            employerGroupId: decodeBase64(this.props.employerGroupId)
          }}
          defaultValue='All'
          value={this.state.employerFilter
            ? this.state.employerFilter
            : 'all'
          }
          onChange={employer => (
            this.setState({
              employerFilter: employer === 'all'
                ? null
                : employer
            })
          )}
        />
      },
      {
        title: 'Direction',
        options: <PicklistWithData
          id='direction'
          forField='WhatsappMessageDirection'
          default='Inbound'
          symbol='chevrondown'
          value={this.state.directionFilter || ''}
          onChange={direction => this.setState({ directionFilter: direction })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='WhatsappMessageStatus'
          default='All'
          symbol='chevrondown'
          value={this.state.statusFilter || ''}
          onChange={status => this.setState({ statusFilter: status })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Message type',
        options: <PicklistWithData
          id='messageType'
          forField='WhatsappMessageMessageType'
          default='All'
          symbol='chevrondown'
          value={this.state.messageTypeFilter || ''}
          onChange={messageType => this.setState({ messageTypeFilter: messageType })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Created between',  // sent or received
        options: <DateRange
          startDate={this.state.createdAtFromFilter}
          endDate={this.state.createdAtToFilter}
          onStartDateChange={date => this.setState({ createdAtFromFilter: date })}
          onEndDateChange={date => this.setState({ createdAtToFilter: date })}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      }
    ]

    if (this.props.identityId) {
      filters.shift()
    } else {
      filters.unshift({
        title: 'Search',
        options: <Search
          id='search'
          value={this.state.searchString}
          placeholder='Enter string'
          onChange={e => this.setState({ searchString: e.target.value })}
          onClear={() => this.setState({ searchString: '' })}
          onClick={() => this.setState({ searchFilter: this.state.searchString })}
        />
      })
    }

    return filters
  }

  displayListing = (communicationType) => {
    const { fromAddrFilter, toAddrFilter, employerFilter, statusFilter, directionFilter, messageTypeFilter, orderBy,
      timestampFromFilter, timestampToFilter, createdAtFromFilter, createdAtToFilter, searchFilter} = this.state

    switch(communicationType) {
      case 'SMS':
        const fromAddr = fromAddrFilter
        const toAddr = toAddrFilter

        const isInboundMessage = directionFilter === 'inbound'

        return (
          <Messages
            fromEmployerGroupId={isInboundMessage
              ? undefined
              : this.props.employerGroupId
            }
            toEmployerGroupId={isInboundMessage
              ? this.props.employerGroupId
              : undefined
            }
            fromEmployerId={isInboundMessage
              ? undefined
              : employerFilter
            }
            toEmployerId={isInboundMessage
              ? employerFilter
              : undefined
            }
            status={statusFilter}
            direction={directionFilter}
            messageType={messageTypeFilter}
            fromAddr={fromAddr ? normalizePhoneNumber(fromAddr) : null}
            toAddr={toAddr ? normalizePhoneNumber(toAddr) : null}
            // set from/to identity id props according to direction; identityId gets passed into
            // Communications component when we want to set identity as an unchangeable filter
            fromIdentityId={isInboundMessage
              ? undefined
              : this.props.identityId
            }
            toIdentityId={isInboundMessage
              ? this.props.identityId
              : undefined
            }
            // scheduledAtFrom={scheduledAtFromFilter
            //   ? moment.utc(scheduledAtFromFilter).toISOString()
            //   : null
            // }
            // scheduledAtTo={scheduledAtToFilter
            //   ? moment.utc(scheduledAtToFilter).toISOString()
            //   : null
            // }
            timestampFrom={timestampFromFilter
              ? moment.utc(timestampFromFilter).toISOString()
              : null
            }
            timestampTo={timestampToFilter
              ? moment.utc(timestampToFilter).toISOString()
              : null
            }
            unknownParticipant={false}
            orderBy={orderBy}
            changeSort={this.changeOrderBy}
            // actionColumn={{
            //   name: 'Related case',
            //   render: this.openOrAttachCase
            // }}
          />
        )

      case 'WhatsApp':
        return (
          <EmployerWhatsAppMessages
            fromEmployerGroupId={isInboundMessage
              ? undefined
              : this.props.employerGroupId
            }
            toEmployerGroupId={isInboundMessage
              ? this.props.employerGroupId
              : undefined
            }
            fromEmployerId={isInboundMessage
              ? undefined
              : employerFilter
            }
            toEmployerId={isInboundMessage
              ? employerFilter
              : undefined
            }
            fromIdentityId={isInboundMessage
              ? this.props.identityId
              : undefined
            }
            toIdentityId={isInboundMessage
              ? undefined
              : this.props.identityId
            }
            direction={directionFilter}
            status={statusFilter}
            messageType={messageTypeFilter}
            createdAtStartDate={createdAtFromFilter
              ? moment.utc(createdAtFromFilter).toISOString()
              : null
            }
            createdAtEndDate={createdAtToFilter
              ? moment.utc(createdAtToFilter).toISOString()
              : null
            }
            search={searchFilter}
            orderBy={orderBy}
            changeSort={this.changeOrderBy}
          />
        )

      default:
        return null
    }
  }

  getAssociatedContentType = (communicationType) => {
    if (communicationType === 'SMS') {
      return CONTENTTYPE_MESSAGE
    }

    if (communicationType === 'WhatsApp') {
      return CONTENTTYPE_WHATSAPPMESSAGE
    }

    return null
  }

  render() {
    let tabs = ['SMS', 'WhatsApp']
    let filters = this.getFilters(this.state.communicationType || 'SMS')

    return (
      <div className='slds-grow slds-scrollable_y' role="main">
        <div className='slds-grid slds-wrap slds-size_1-of-1'>
          <div className='tab-nav slds-size_1-of-1 u-bg-color-white product-listing-page-header'>
            {this.props.title !== null &&
              <div className='slds-m-top_small slds-p-left_small product-listing-page-header_title'>
                {this.props.title}
              </div>
            }
            {this.props.noStatusFilter
              ? null
              : this.props.lockOnCommunicationType === false &&
                  <TabbedView
                    tabs={tabs}
                    contents={[]}
                    selectedTab={tabs.indexOf(this.state.communicationType)}
                    onClick={tabIndex => this.setState(prevState => {
                      let communicationType = tabs[tabIndex]
                      if (prevState.communicationType !== communicationType) {
                        return {
                          // shared filters
                          communicationType,
                          statusFilter: null,
                          directionFilter: 'inbound',
                          // scheduledAtFromFilter: null,
                          // scheduledAtToFilter: null,
                          fromAddrFilter: null,
                          orderBy: communicationType === 'SMS'
                            ? '-timestamp'
                            : '-created_at',
                          // message filters
                          messageTypeFilter: null,
                          timestampFromFilter: null,
                          timestampToFilter: null,
                          toAddrFilter: null,
                          // email filters
                          createdAtFromFilter: null,
                          createdAtToFilter: null,
                          // from/to identity
                          // from/to addr
                          // has attachments?
                        }
                      }
                    })}
                    tabsClassName='slds-align_absolute-center product-listing-page-header_tabs'
                  />
            }
          </div>
          <FilterSidebar
            filters={filters}
            filtersApplied={[
                this.state.employerFilter,
                this.state.statusFilter || '',
                this.state.directionFilter || '',
                this.state.messageTypeFilter || '',
                this.state.timestampFromFilter || this.state.timestampToFilter
                  ? this.state.timestampFromFilter
                    ? this.state.timestampFromFilter.toString()
                    : this.state.timestampToFilter.toString()
                  : '',
                this.state.createdAtFromFilter || this.state.createdAtToFilter
                  ? this.state.createdAtFromFilter
                    ? this.state.createdAtFromFilter.toString()
                    : this.state.createdAtToFilter.toString()
                  : '',
              ].filter(filter => filter.length > 0).length
            }
            onClear={() => this.setState({
              employerFilter: '',
              statusFilter: null,
              directionFilter: 'inbound',
              messageTypeFilter: null,
              fromAddrFilter: undefined,
              toAddrFilter: undefined,
              timestampFromFilter: null,
              timestampToFilter: null,
              createdAtFromFilter: null,
              createdAtToFilter: null
            })}
            key={filters}
          />
          <div className='slds-col slds-size_10-of-12 slds-scrollable_x slds-border_top'>
            {/* consider this.props.communicationType above this.state.communicationType when status filter
            value gets determined and passed into this component instead of relying on internal tabbed view */}
            {this.displayListing(this.props.communicationType || this.state.communicationType)}
          </div>
        </div>
      </div>
    )
  }
}

export default withRouter(EmployerGroupCommunications)



// WEBPACK FOOTER //
// ./src/components/messageplus/employergroup-communications/index.js